/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2020. All rights reserved
------------------------------------------------------------------------------------------------- */
import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { KEYCODE } from '../../utils/keycode.js';
/**
 * @prop {String} label - Defines the drawer's title.
 * @prop {Boolean} visible - Defines whether the drawer is visible or hidden. Adding this attribute to the drawer will trigger it's visibility, and clicking outside of it or on the close icon will remove this attribute from the component.
 * @prop {String} position - Defines the position of the drawer on the page. Possible values are `left`, `right`, `top` and `bottom`.
 * @prop {Boolean} sticky - Defines that the drawer can only be closed through actions present in the drawer.
 * @prop {String} drawerWidth - Defines the width of the drawer container for drawers with position equal to left or right. Can accept standard css length values.
 * @slot header - Takes children such as tabs.
 * @slot unnamed - Slot where the main content is rendered. Takes any component as child.
 * @slot footer - Takes buttons as children.
 * @slot functions - Takes icons as children.
 * @fires drawer-closed - Dispatched when closing the drawer.
 */
export class SHDrawer extends LitElement {
  static get properties() {
    return {
      visible: { type: Boolean, reflect: true },
      label: { type: String, reflect: true },
      position: { type: String, reflect: true },
      sticky: { type: Boolean, reflect: true },
      drawerWidth: { type: String, reflect: true, attribute: 'drawer-width' },
      _emptyHeader: { type: Boolean },
      _emptyFooter: { type: Boolean },
      _emptyFunction: { type: Boolean },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          position: fixed;
          top: 0rem;
          left: 0rem;
          height: 100%;
          width: 100%;
          background: rgba(0, 0, 0, 0.4);
          /**
          Setting z-index is necessary here inorder to make the drawer appear.
          
          Why the value is set to 6?

          Since
            pane has z-index=2,
            access-bar has z-index=4,
            a reasonable value of 6 is chosen. 
          
          Why not a high value like that of popover ?
          
          Since we have no concrete control in what way the BL intends to display its popovers
          (whether it has to appear above the drawers or below it), we are not choosing such high values.

          Since the z-index is applied directly to the host, the Business Line can any time
          modify the z-index according to the business needs.
         */
          z-index: 6;
          max-height: 0rem;
          opacity: 0;
          transition: var(--transition-time) all ease-in-out,
            0s height ease-in-out var(--transition-time),
            0s max-height ease-in-out var(--transition-time), 0s visibility linear 0s;
          display: block;
          visibility: hidden;
        }
        :host(:not([visible])) {
          overflow: hidden;
          pointer-events: none;
        }
        .drawer-wrapper {
          background: var(--base-3);
          position: absolute;
          display: flex;
          flex-direction: column;
          box-shadow: var(--shadow-navigation);
          box-sizing: border-box;
          transition: var(--transition-time) all ease-in-out;
          --drawer-width: 22rem;
        }
        .drawer-wrapper > ::slotted(*) {
          margin-bottom: 0.5rem;
        }
        /* slot styles */
        :host(:not([label])) .drawer-wrapper[empty-function] .label-wrapper {
          display: none;
        }
        .drawer-wrapper:not([empty-header]) .label-wrapper {
          display: none;
        }
        .header-wrapper {
          display: flex;
          flex-direction: row;
          padding: 1rem;
        }
        .label-wrapper {
          display: flex;
          flex-direction: row;
          align-items: center;
          padding: 1rem;
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
        }
        .functions-wrapper {
          display: flex;
          flex-direction: row;
          align-items: center;
        }
        .body-wrapper {
          flex: 1;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          overflow: auto;
          overflow-x: hidden;
          padding: 0 1rem;
        }
        .drawer-wrapper[empty-header] .body-wrapper {
          padding-top: 1rem;
        }
        .drawer-wrapper[empty-footer] .body-wrapper {
          padding-bottom: 1rem;
        }
        .body-wrapper ::slotted(*:not(:first-child)) {
          margin-top: 0.5rem;
        }
        .body-wrapper ::slotted(sh-menu-item) {
          margin-bottom: 0rem;
        }
        .footer-wrapper {
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
          padding: 1rem;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          justify-content: flex-end;
          align-items: center;
        }
        .functions-wrapper > ::slotted(*),
        .footer-wrapper > ::slotted(*) {
          margin-left: 0.5rem;
        }
        .drawer-wrapper[empty-header] .header-wrapper {
          display: none;
        }
        .drawer-wrapper:not([empty-header]) .body-wrapper,
        :host([label]) .body-wrapper {
          padding-top: 0rem;
        }
        .drawer-wrapper[empty-footer] .footer-wrapper {
          display: none;
        }
        /* positioning */
        :host([position='left']) .drawer-wrapper {
          left: 0rem;
          transform: translateX(-5rem);
        }
        :host([position='right']) .drawer-wrapper {
          right: 0rem;
          transform: translateX(5rem);
        }
        :host([position='top']) .drawer-wrapper,
        :host([position='bottom']) .drawer-wrapper {
          height: auto;
          min-height: 2.5rem;
          max-height: calc(100% - 6.5rem);
          width: 100%;
          max-width: 100%;
        }
        :host([position='left']) .drawer-wrapper,
        :host([position='right']) .drawer-wrapper {
          width: var(--drawer-width);
          height: 100%;
        }
        :host([position='top']) .drawer-wrapper {
          top: 0rem;
          transform: translateY(-5rem);
        }
        :host([position='bottom']) .drawer-wrapper {
          bottom: 0rem;
          transform: translateY(5rem);
        }
        /* Margin constraint */
        :host([position='left']) .drawer-wrapper,
        :host([position='right']) .drawer-wrapper {
          max-width: calc(100% - 6.5rem);
        }
        /* visible */
        :host([visible]) {
          max-height: 100%;
          opacity: 1;
          transition: var(--transition-time) all ease-in-out var(--transition-time),
            0s height ease-in-out, 0s max-height ease-in-out, 0s visibility linear 0s;
          visibility: visible;
        }
        :host([visible]) .drawer-wrapper {
          transition: var(--transition-time) all ease-in-out var(--transition-time);
          transform: none;
          min-height: 10rem;
        }
        .drawer-label {
          overflow: hidden;
          width: -webkit-fill-available;
          width: -moz-available;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: var(--text-primary);
          font: var(--header-2);
          line-height: 1.5rem;
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
          -moz-osx-font-smoothing: grayscale;
          height: 1.5rem;
        }
        /* touch */
        @media (any-pointer: coarse) {
          .functions-wrapper > ::slotted(*),
          .footer-wrapper > ::slotted(*) {
            margin-left: 1rem;
          }
        }
        :host(.point5-vertical-padding) .body-wrapper {
          padding: 0.5rem 1rem;
        }
        :host([position='bottom'][visible].initial-slide-on-connect) {
          animation: fade-in var(--transition-time) ease-in-out;
        }
        :host([position='bottom'][visible].initial-slide-on-connect) .drawer-wrapper {
          animation: slide-on-connect var(--transition-time) ease-in-out;
        }
        @keyframes slide-on-connect {
          0% {
            bottom: 0rem;
            transform: translateY(5rem);
          }
          100% {
            transform: none;
          }
        }
        @keyframes fade-in {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
      `,
    ];
  }

  render() {
    return html`
      <div
        class="drawer-wrapper"
        id="content"
        ?empty-footer="${this._emptyFooter}"
        ?empty-header="${this._emptyHeader}"
        ?empty-function="${this._emptyFunction}"
      >
        <div class="label-wrapper">
          ${this.label
            ? html`<div class="drawer-label">${this.label}</div>`
            : html`<div class="drawer-label"></div>`}
          <div class="functions-wrapper">
            <slot
              name="functions"
              id="functions"
              @slotchange="${(e) => this._updateSlotFlag(e.target, '_emptyFunction')}"
            ></slot>
          </div>
        </div>
        <div class="header-wrapper">
          <slot
            name="header"
            id="header"
            @slotchange="${(e) => this._updateSlotFlag(e.target, '_emptyHeader')}"
          ></slot>
        </div>
        <div class="body-wrapper">
          <slot id="body"></slot>
        </div>
        <div class="footer-wrapper">
          <slot
            name="footer"
            id="footer"
            @slotchange="${(e) => this._updateSlotFlag(e.target, '_emptyFooter')}"
          ></slot>
        </div>
      </div>
    `;
  }

  constructor() {
    super();
    this.position = 'left';
    this._emptyHeader = true;
    this._emptyFooter = true;
    this._emptyFunction = true;
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('visible')) {
      this._drawerStatus();
    }
    if (changedProperties.has('drawerWidth')) {
      this.shadowRoot
        .querySelector('.drawer-wrapper')
        .style.setProperty('--drawer-width', this.drawerWidth);
    }
    const listOfProperties = ['visible', 'label', 'position', 'sticky', 'drawerWidth'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  firstUpdated() {
    super.firstUpdated();
    this.addEventListener('mousedown', function () {
      if (!this.sticky) {
        this._closeDrawer();
      }
    });
    this.shadowRoot.querySelector('#content').addEventListener('mousedown', function (e) {
      e.stopPropagation();
    });
    this._documentKeyupListenerRef = this._handleDocumentKeyup.bind(this);
  }
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', 0);
    }
  }
  disconnectedCallback() {
    document.removeEventListener('keyup', this._documentKeyupListenerRef);
    super.disconnectedCallback();
  }
  _updateSlotFlag(slot, emptySlotFlag) {
    const numberOfNodes = slot.assignedNodes({
      flatten: true,
    }).length;
    if (numberOfNodes === 0) {
      this[emptySlotFlag] = true;
    } else {
      this[emptySlotFlag] = false;
    }
  }
  _closeDrawer() {
    this.visible = false;
    // fire event on closing
    dispatchCustomEvent(this, 'drawer-closed');
    console.warn(
      `The drawer-closed event is a legacy event of sh-drawer component and will be deprecated soon.\nPlease use the visible-changed event to observe changes in visible property`
    );
  }

  _drawerStatus() {
    if (this.visible) {
      //focus inside the drawer to move out of natural tab order
      this.focus();
      this.style.outline = '0';
      document.addEventListener('keyup', this._documentKeyupListenerRef);
    } else {
      document.removeEventListener('keyup', this._documentKeyupListenerRef);
    }
  }
  _handleDocumentKeyup(e) {
    /**
     * Preventing sticky drawer from closing when pressed on Escape
     * https://dev.azure.com/shs-ti-ux/SHUI/_workitems/edit/26790
     */
    if (e.keyCode === KEYCODE.ESCAPE && !this.sticky) {
      this._closeDrawer();
    }
  }
}
window.customElements.define('sh-drawer', SHDrawer);
